// ---------------------------------------------------------------------------
// UI Slice — editor chrome state (panels, mode)
// ---------------------------------------------------------------------------

import type { LeftPanelTab } from './types'
import type { StepPath } from './flow-slice'

/** Context for an unbound event the user clicked in the hierarchy tree. */
export interface SelectedEventContext {
  blockName: string
  elementId: string
  eventType: string
}

export interface UiSlice {
  leftPanelOpen: boolean
  rightPanelOpen: boolean
  activeLeftTab: LeftPanelTab
  hoveredNodeId: string | null
  selectedEdgeId: string | null

  // CrossTabSync — highlight state shared across tabs
  highlightedFlowId: string | null
  highlightedEventId: string | null
  highlightedComponentId: string | null
  setHighlightedFlowId: (id: string | null) => void
  setHighlightedEventId: (id: string | null) => void
  setHighlightedComponentId: (id: string | null) => void

  // Unbound event context — tracks which event the user wants to create/bind a flow for
  selectedEventContext: SelectedEventContext | null
  setSelectedEventContext: (ctx: SelectedEventContext | null) => void

  // Step selection + Inspector panel
  selectedStepIndex: number | null
  setSelectedStepIndex: (index: number | null) => void
  selectedStepPath: StepPath | null
  setSelectedStepPath: (path: StepPath | null) => void
  inspectorOpen: boolean
  setInspectorOpen: (open: boolean) => void
  toggleInspector: () => void

  toggleLeftPanel: () => void
  toggleRightPanel: () => void
  setActiveLeftTab: (tab: LeftPanelTab) => void
  setHoveredNode: (id: string | null) => void
  setSelectedEdgeId: (id: string | null) => void

  // Phase 003 (v1.0): Flow map canvas selection + filter state.
  // Feature is wired in flow-map-canvas.tsx but slice was never typed.
  // See apps/cogo-studio/docs/blockdef-schema.md.
  selectedMapFlowName: string | null
  setSelectedMapFlowName: (name: string | null) => void
  pageFilter: string | null
}

// NOTE: Set receives the FULL merged state (FlowSlice & UiSlice) because of
// immer middleware in flow-editor-store.ts. We type it as `any` here so we can
// clear selectedFlowId cross-slice when setting event context.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Set = (fn: (state: any) => void) => void
type Get = () => UiSlice

export function createUiSlice(set: Set, _get: Get, _api: unknown): UiSlice {
  return {
    leftPanelOpen: true,
    rightPanelOpen: true,
    activeLeftTab: 'event-flow',
    hoveredNodeId: null,
    selectedEdgeId: null,

    // CrossTabSync
    highlightedFlowId: null,
    highlightedEventId: null,
    highlightedComponentId: null,

    // Unbound event context
    selectedEventContext: null,

    // Step selection + Inspector
    selectedStepIndex: null,
    selectedStepPath: null,
    inspectorOpen: false,

    // Phase 003 (v1.0): Flow map canvas
    selectedMapFlowName: null,
    pageFilter: null,

    setSelectedMapFlowName: (name) =>
      set((state) => {
        state.selectedMapFlowName = name
      }),

    setSelectedStepIndex: (index) =>
      set((state) => {
        state.selectedStepIndex = index
      }),

    setSelectedStepPath: (path) =>
      set((state) => {
        state.selectedStepPath = path
        // Also update legacy selectedStepIndex for backward compat
        state.selectedStepIndex = path != null ? path[0] : null
      }),

    setInspectorOpen: (open) =>
      set((state) => {
        state.inspectorOpen = open
      }),

    toggleInspector: () =>
      set((state) => {
        state.inspectorOpen = !state.inspectorOpen
      }),

    setHighlightedFlowId: (id) =>
      set((state) => {
        state.highlightedFlowId = id
      }),

    setHighlightedEventId: (id) =>
      set((state) => {
        state.highlightedEventId = id
      }),

    setHighlightedComponentId: (id) =>
      set((state) => {
        state.highlightedComponentId = id
      }),

    setSelectedEventContext: (ctx) =>
      set((state) => {
        state.selectedEventContext = ctx
        // Clear any previously selected flow so FlowStepPanel enters Create/Bind state
        state.selectedFlowId = null
      }),

    toggleLeftPanel: () =>
      set((state) => {
        state.leftPanelOpen = !state.leftPanelOpen
      }),

    toggleRightPanel: () =>
      set((state) => {
        state.rightPanelOpen = !state.rightPanelOpen
      }),

    setActiveLeftTab: (tab) =>
      set((state) => {
        state.activeLeftTab = tab
        state.leftPanelOpen = true
      }),

    setHoveredNode: (id) =>
      set((state) => {
        state.hoveredNodeId = id
      }),

    setSelectedEdgeId: (id) =>
      set((state) => {
        state.selectedEdgeId = id
      }),
  }
}
